import "./ScrollIndicator.css";
import { useEffect, useRef } from "react";

const MIN_THUMB = 40; // px, so very long pages still get a grabbable thumb
const ACTIVE_MS = 900; // how long it stays bright after scrolling stops

/*
 * The page's scrollbar, drawn on top of the page (the browser's own one is
 * hidden on the dark pages, see index.css). Because it takes no space, the
 * page doesn't shift when scrolling gets locked, e.g. while Contact is open.
 *
 * - A slim pill on the right edge showing where you are on the page; it gets
 *   brighter while scrolling and when the mouse is over it
 * - Drag it to scroll; click above/below it to move a screen up/down
 * - `arrows` (home page): drawn as a thin line with ^ at the top and v at the
 *   bottom, like an arrow pointing both ways; the arrows also move a screen
 *   up/down, and each dims when there's nowhere further to go
 * - Hidden when the page doesn't scroll
 * Position and size are set directly on the elements (no re-renders while
 * scrolling), so it stays smooth.
 */
function ScrollIndicator({ arrows = false }) {
  const trackRef = useRef(null);
  const railRef = useRef(null);
  const thumbRef = useRef(null);

  const page = (direction) =>
    window.scrollBy({ top: direction * window.innerHeight * 0.9, behavior: "smooth" });

  useEffect(() => {
    const track = trackRef.current;
    const rail = railRef.current;
    const thumb = thumbRef.current;
    const root = document.documentElement;
    let frame = null;
    let idleTimer = null;
    let drag = null;
    let size = { room: 0, scrollable: 0 };

    const update = () => {
      frame = null;
      const scrollable = root.scrollHeight - window.innerHeight;
      track.classList.toggle("is-hidden", scrollable <= 1);
      if (scrollable <= 1) return;
      const railHeight = rail.clientHeight;
      const thumbHeight = Math.max(MIN_THUMB, (window.innerHeight / root.scrollHeight) * railHeight);
      const room = railHeight - thumbHeight;
      size = { room, scrollable };
      const scrolled = Math.min(Math.max(window.scrollY, 0), scrollable);
      thumb.style.height = `${thumbHeight}px`;
      thumb.style.transform = `translateY(${(scrolled / scrollable) * room}px)`;
      track.classList.toggle("at-top", scrolled < 2);
      track.classList.toggle("at-bottom", scrolled > scrollable - 2);
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };

    // Brighter while scrolling, then fades back
    const onScroll = () => {
      schedule();
      track.classList.add("is-active");
      clearTimeout(idleTimer);
      idleTimer = setTimeout(() => {
        if (!drag) track.classList.remove("is-active");
      }, ACTIVE_MS);
    };

    const onPointerDown = (event) => {
      if (event.button !== 0) return;
      event.preventDefault();
      if (event.target === thumb) {
        // Drag the thumb: the page follows
        drag = { y: event.clientY, scroll: window.scrollY };
        rail.setPointerCapture(event.pointerId);
        track.classList.add("is-dragging", "is-active");
      } else {
        // Click the rail: one screen up or down, toward the click
        page(event.clientY < thumb.getBoundingClientRect().top ? -1 : 1);
      }
    };
    const onPointerMove = (event) => {
      if (!drag || !size.room) return;
      const moved = event.clientY - drag.y;
      window.scrollTo({ top: drag.scroll + (moved / size.room) * size.scrollable, behavior: "instant" });
    };
    const onPointerUp = () => {
      if (!drag) return;
      drag = null;
      track.classList.remove("is-dragging");
      onScroll(); // fade back after a moment
    };

    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", schedule);
    // The page grows/shrinks as images load or sections change
    const observer = new ResizeObserver(schedule);
    observer.observe(document.body);
    rail.addEventListener("pointerdown", onPointerDown);
    rail.addEventListener("pointermove", onPointerMove);
    rail.addEventListener("pointerup", onPointerUp);
    rail.addEventListener("pointercancel", onPointerUp);

    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(idleTimer);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", schedule);
      observer.disconnect();
      rail.removeEventListener("pointerdown", onPointerDown);
      rail.removeEventListener("pointermove", onPointerMove);
      rail.removeEventListener("pointerup", onPointerUp);
      rail.removeEventListener("pointercancel", onPointerUp);
    };
  }, []);

  return (
    <div className={`scroll-indicator ${arrows ? "has-arrows" : ""}`} ref={trackRef}>
      {arrows ? (
        <button
          className="scroll-indicator-arrow is-up"
          onClick={() => page(-1)}
          aria-label="Scroll up"
          tabIndex={-1}
        >
          <svg viewBox="0 0 12 8" aria-hidden="true">
            <path d="M1 7l5-5 5 5" />
          </svg>
        </button>
      ) : null}
      <div className="scroll-indicator-rail" ref={railRef} aria-hidden="true">
        <div className="scroll-indicator-thumb" ref={thumbRef} />
      </div>
      {arrows ? (
        <button
          className="scroll-indicator-arrow is-down"
          onClick={() => page(1)}
          aria-label="Scroll down"
          tabIndex={-1}
        >
          <svg viewBox="0 0 12 8" aria-hidden="true">
            <path d="M1 1l5 5 5-5" />
          </svg>
        </button>
      ) : null}
    </div>
  );
}

export default ScrollIndicator;
